import { extend, uid, isDefined } from "@dhx/ts-common/core";
import { el } from "@dhx/ts-common/dom";
import { EventSystem } from "@dhx/ts-common/events";
import { keyManager } from "@dhx/ts-common/Keymanager";
import { IHandlers } from "@dhx/ts-common/types";
import { Layout } from "@dhx/ts-layout";
import { ItemType, NavigationBarEvents, Toolbar } from "@dhx/ts-toolbar";
import { IResizeConfig, IWindowConfig, WindowEvents, IWindow, ISize, IPosition } from "./types";
import WindowController from "./WindowController";

function detectDrag(e: MouseEvent) {
	return new Promise<any>(res => {
		const timeout = setTimeout(() => {
			handleMouseUp();
		}, 1000);
		const handleMouseUp = () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
			res(false);
		};
		const handleMouseMove = moveEvent => {
			if (Math.abs(moveEvent.pageX - e.pageX) > 4 || Math.abs(moveEvent.pageY - e.pageY) > 4) {
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
				clearTimeout(timeout);
				res({ x: e.pageX, y: e.pageY });
			}
		};
		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);
	});
}

export class Window implements IWindow{
	public config: IWindowConfig;
	public events: EventSystem<WindowEvents>;

	public header: Toolbar;
	public footer: Toolbar;

	private _popup: HTMLElement;
	private _uid: string;

	private _handlers: IHandlers;

	private _layout: Layout;
	private _isActive: boolean;
	private _blocker: HTMLElement;

	constructor(config: IWindowConfig) {
		this.config = extend({
			movable: false,
			resizable: false,
			header: false,
			closable: config.modal,
			minWidth: 100,
			minHeight: 100
		}, config);

		this._uid = uid();
		this.events = new EventSystem(this);

		const popup = this._popup = document.createElement("div");
		popup.tabIndex = 1;

		if (!this.config.modal) {
			WindowController.add(this._uid, this._popup);
		}
		this._isActive = false;

		this._initHandlers();
		this._initUI();

		if (this.config.html) {
			this.attachHTML(this.config.html);
		}
	}
	paint(): void {
		this._layout.paint();
	}
	setFullScreen(): void {
		this.setSize(window.innerWidth, window.innerHeight);
		this.setPosition(window.pageXOffset, window.pageYOffset);
	}
	// TODO: remove sute_7.0
	fullScreen(): void {
		this.setFullScreen();
	}
	setSize(width: number, height: number): void {
		const oldsize = {
			width: this._popup.offsetWidth,
			height: this._popup.offsetHeight
		};
		const size = {
			width: oldsize.width,
			height: oldsize.height
		};
		if (isDefined(width)) {
			this.config.width = size.width = width;
		}
		if (isDefined(height)) {
			this.config.height = size.height = height;
		}

		this._popup.style.width = size.width + "px";
		this._popup.style.height = size.height + "px";

		this.events.fire(WindowEvents.resize, [
			size,
			oldsize,
			{ left: true, top: true, bottom: true, right: true }
		]);
	}
	getSize(): ISize {
		return {
			width: this._popup.offsetWidth,
			height: this._popup.offsetHeight
		};
	}
	setPosition(left: number, top: number): void {
		const oldposition = {
			left: this._popup.offsetLeft,
			top: this._popup.offsetTop,
		};
		const position = {
			left: oldposition.left,
			top: oldposition.top
		};
		if (isDefined(left)) {
			this.config.left = position.left = left;
		}
		if (isDefined(top)) {
			this.config.top = position.top = top;
		}

		this._popup.style.left = position.left + "px" ;
		this._popup.style.top = position.top + "px";

		this.events.fire(WindowEvents.resize, [
			position,
			oldposition,
			{ left: true, top: true, bottom: true, right: true }
		]);
	}
	getPosition(): IPosition {
		return {
			left: this._popup.offsetLeft,
			top: this._popup.offsetTop,
		};
	}
	show(left: number = this.config.left, top: number = this.config.top): void {
		if (!this.events.fire(WindowEvents.beforeShow, [left, top])) {
			return;
		}
		this._popup.className = "dhx_popup dhx_widget dhx_popup--window" + (this.config.modal ? " dhx_popup--window_modal" : "") + (this.config.css ? " " + this.config.css : "");
		this._popup.style.position = this.config.modal ? "fixed" : "absolute";

		const height = this.config.height = this.config.height || this.config.minHeight || window.innerHeight / 2;
		const width = this.config.width = this.config.width || this.config.minWidth || window.innerWidth / 2;

		this.config.left = left = isDefined(left) ? left : (window.innerWidth - width) / (this.config.modal ? 2 : 2 + window.pageXOffset);
		this.config.top = top = isDefined(top) ? top : (window.innerHeight - height) / (this.config.modal ? 2 : 2 + window.pageYOffset);

		if (this._isActive) {
			this._popup.style.left = left + "px";
			this._popup.style.top = top + "px";
			return;
		}
		if (this.config.viewportOverflow) {
			WindowController.openFreeWindow();
		}
		if (this.config.modal) {
			this._blockScreen();
		}

		this._popup.style.width = width + "px";
		this._popup.style.height = height + "px";
		this._popup.style.left = left + "px";
		this._popup.style.top = top + "px";
		document.body.appendChild(this._popup);
		this._popup.focus();
		this._isActive = true;
		this.events.fire(WindowEvents.afterShow, []);
	}
	hide(): void {
		if (!this._isActive || !this.events.fire(WindowEvents.beforeHide, [])) {
			return;
		}
		if (this.config.viewportOverflow) {
			WindowController.closeFreeWindow();
		}
		if (this._blocker) {
			document.body.removeChild(this._blocker);
			if (this.config.closable) {
				keyManager.removeHotKey(null, this);
			}
			this._blocker = null;
		}
		document.body.removeChild(this._popup);
		this._isActive = false;
		this.events.fire(WindowEvents.afterHide, []);
	}
	isVisible(): boolean {
		return this._isActive;
	}
	getWidget(): any {
		return this._layout.getCell("content").getWidget();
	}
	getContainer(): HTMLElement {
		return this.getRootView().data._container;
	}
	attach(name: any, config?: any): void {
		this._layout.getCell("content").attach(name, config);
	}
	attachHTML(html: string): void {
		this._layout.getCell("content").attach(
			() => el(".dhx_window__inner-html-content", {
				".innerHTML": html
			})
		);
	}
	getRootView() {
		return this._layout.getRootView();
	}
	destructor(): void {
		if (this._isActive) {
			this.hide();
		}
		if (this.header) {
			this.header.destructor();
		}
		if (this.footer) {
			this.footer.destructor();
		}
		this._layout.destructor();
		this._popup = null;
	}
	protected _initHandlers() {
		this._handlers = {
			headerDblClick: () => this.events.fire(WindowEvents.headerDoubleClick, []),
			move: e => {
				if (e.which === 3) {
					return;
				}
				e.preventDefault();
				WindowController.setActive(this._uid);

				detectDrag(e).then(pos => {
					if (pos) {
						this._startDrag(pos.x, pos.y);
					}
				});
			},
			resize: {
				".dhx_window-resizer": e => {
					if (e.which === 3) {
						return;
					}
					e.preventDefault();
					WindowController.setActive(this._uid);

					detectDrag(e).then(pos => {
						if (pos) {
							const classList = (e.target as HTMLElement).classList;
							if (classList.contains("dhx_window-resizer--left")) {
								this._startResize({ left: true });
							} else if (classList.contains("dhx_window-resizer--right")) {
								this._startResize({ right: true });
							} else if (classList.contains("dhx_window-resizer--top")) {
								this._startResize({ top: true });
							} else if (classList.contains("dhx_window-resizer--bottom")) {
								this._startResize({ bottom: true });
							} else if (classList.contains("dhx_window-resizer--bottom_left")) {
								this._startResize({ left: true, bottom: true });
							} else if (classList.contains("dhx_window-resizer--bottom_right")) {
								this._startResize({ bottom: true, right: true });
							} else if (classList.contains("dhx_window-resizer--top_left")) {
								this._startResize({ top: true, left: true });
							} else if (classList.contains("dhx_window-resizer--top_right")) {
								this._startResize({ top: true, right: true });
							}
						}
					});
				}
			},
			setActive: () => {
				WindowController.setActive(this._uid);
			},
		};
	}
	private _initUI() {
		const rows:any = [];
		const isHeader = this.config.header || this.config.title || this.config.closable || this.config.movable;

		if (isHeader) {
			rows.push({
				id: "header",
				gravity: false,
				css: "dhx_window-header " + (this.config.movable ? "dhx_window-header--movable" : "") ,
				on: {
					mousedown: this.config.movable && this._handlers.move,
					dblclick: this._handlers.headerDblClick,
				}
			});
		}
		rows.push({ id: "content", css: isHeader ? "dhx_window-content" : "dhx_window-content-without-header"});
		if (this.config.footer) {
			rows.push({ id: "footer", gravity: false, css: "dhx_window-footer" });
		}
		if (this.config.resizable) {
			rows.push({ id: "resizers", gravity: false, css: "resizers" });
		}
		const layout = this._layout = new Layout(this._popup, {
			css: "dhx_window" + (this.config.modal ? " dhx_window--modal" : ""),
			rows,
			on: {
				click: this._handlers.setActive
			}
		});
		if (isHeader) {
			const header = this.header = new Toolbar();
			if (this.config.title) {
				this.header.data.add({
					type: ItemType.title,
					value: this.config.title,
					id: "title",
				});
			}
			if (this.config.closable) {
				this.header.data.add({
					type: ItemType.spacer
				});
				this.header.data.add({
					id: "close",
					type: ItemType.button,
					view: "link",
					size: "medium",
					color: "secondary",
					circle: true,
					icon: "dxi dxi-close"
				});
				header.events.on(NavigationBarEvents.click, id => {
					if (id === "close") {
						this.hide();
					}
				});
			}
			layout.getCell("header").attach(header);
		}
		if (this.config.footer) {
			const footer = this.footer = new Toolbar();
			layout.getCell("footer").attach(footer);
		}
		if (this.config.resizable) {
			layout.getCell("resizers").attach(() => this._drawResizers());
		}
	}
	private _drawResizers() {
		return el(".dhx-resizers", {
			onmousedown: this._handlers.resize
		}, [
			el(".dhx_window-resizer.dhx_window-resizer--left"),
			el(".dhx_window-resizer.dhx_window-resizer--right"),
			el(".dhx_window-resizer.dhx_window-resizer--bottom"),
			el(".dhx_window-resizer.dhx_window-resizer--top"),
			el(".dhx_window-resizer.dhx_window-resizer--bottom_right"),
			el(".dhx_window-resizer.dhx_window-resizer--bottom_left"),
			el(".dhx_window-resizer.dhx_window-resizer--top_right"),
			el(".dhx_window-resizer.dhx_window-resizer--top_left")
		]);
	}
	private _startDrag(x: number, y: number) {
		document.body.classList.add("dhx_window--stop_selection");

		const deltaX = x - this._popup.offsetLeft;
		const deltaY = y - this._popup.offsetTop;
		const width = this._popup.offsetWidth;
		const height = this._popup.offsetHeight;

		const mousemove = e => {
			const oldposition = {
				left: this._popup.offsetLeft,
				top: this._popup.offsetTop,
			};
			const initX = window.pageXOffset;
			const initY = window.pageYOffset;

			let newX = e.pageX - deltaX;
			let newY = e.pageY - deltaY;

			if (!this.config.viewportOverflow) {
				if (newX < initX) {
					newX = initX;
				} else if (newX > initX + window.innerWidth - width) {
					newX = initX + window.innerWidth - width;
				}
				if (newY < initY) {
					newY = initY;
				} else if (newY > initY + window.innerHeight - height) {
					newY = initY + window.innerHeight - height;
				}
			}
			this.config.left = newX;
			this.config.top = newY;
			this._popup.style.left = newX + "px";
			this._popup.style.top = newY + "px";
			const position = {left: newX, top: newY};
			this.events.fire(WindowEvents.move, [
				position,
				oldposition,
				{ left: true, top: true, bottom: true, right: true }
			]);
		};
		const mouseup = () => {
			document.removeEventListener("mouseup", mouseup);
			document.removeEventListener("mousemove", mousemove);
			document.body.classList.remove("dhx_window--stop_selection");
		};
		document.addEventListener("mouseup", mouseup);
		document.addEventListener("mousemove", mousemove);
	}
	private _startResize(resizeConfig: IResizeConfig) {
		const minWidth = this.config.minWidth;
		const minHeight = this.config.minHeight;
		const left = this._popup.offsetLeft;
		const top = this._popup.offsetTop;
		const width = this._popup.offsetWidth;
		const height = this._popup.offsetHeight;

		let resizeClassName;
		switch(true) {
			case resizeConfig.bottom && resizeConfig.left:
				resizeClassName = "dhx_window-body-pointer--bottom_left";
				break;
			case resizeConfig.bottom && resizeConfig.right:
				resizeClassName = "dhx_window-body-pointer--bottom_right";
				break;
			case resizeConfig.top && resizeConfig.left:
				resizeClassName = "dhx_window-body-pointer--top_left";
				break;
			case resizeConfig.top && resizeConfig.right:
				resizeClassName = "dhx_window-body-pointer--top-right";
				break;
			case resizeConfig.top:
				resizeClassName = "dhx_window-body-pointer--top";
				break;
			case resizeConfig.bottom:
				resizeClassName = "dhx_window-body-pointer--bottom";
				break;
			case resizeConfig.left:
				resizeClassName = "dhx_window-body-pointer--left";
				break;
			case resizeConfig.right:
				resizeClassName = "dhx_window-body-pointer--right";
				break;
		}

		document.body.classList.add("dhx_window--stop_selection");
		document.body.classList.add(resizeClassName);
		const mousemove = (e: MouseEvent) => {
			const size = {
				width: e.pageX - left,
				height: e.pageY - top,
				left: e.pageX,
				top: e.pageY
			};

			if (resizeConfig.right) {
				if (size.width < minWidth) {
					size.width = minWidth;
				} else if (size.width > window.pageXOffset + window.innerWidth - left) {
					size.width = window.pageXOffset + window.innerWidth - left;
				}

				this._popup.style.width = size.width + "px";
			}
			if (resizeConfig.bottom) {
				if (size.height < minHeight) {
					size.height = minHeight;
				} else if (size.height > window.pageYOffset + window.innerHeight - top) {
					size.height = window.pageYOffset + window.innerHeight - top;
				}

				this._popup.style.height = size.height + "px";
			}
			if (resizeConfig.left) {
				if (left + width - size.left < minWidth) {
					size.left = left + width - minWidth;
				}
				size.width = left + width - size.left;

				this.config.left = size.left;
				this._popup.style.left = size.left + "px";
				this._popup.style.width = size.width + "px";
			}
			if (resizeConfig.top) {
				if (size.top < window.pageYOffset) {
					size.top = window.pageYOffset;
				} else if (top + height - size.top < minHeight) {
					size.top = top + height - minHeight;
				}
				size.height = top + height - size.top;

				this.config.top = size.top;
				this._popup.style.top = size.top + "px";
				this._popup.style.height = size.height + "px";
			}
			this.config.width = this._popup.offsetWidth;
			this.config.height = this._popup.offsetHeight;
			this.events.fire(WindowEvents.resize, [size, { left, top, height, width }, resizeConfig]);
		};
		const mouseup = () => {
			document.removeEventListener("mouseup", mouseup);
			document.removeEventListener("mousemove", mousemove);
			document.body.classList.remove("dhx_window--stop_selection");
			document.body.classList.remove(resizeClassName);
		};
		document.addEventListener("mouseup", mouseup);
		document.addEventListener("mousemove", mousemove);

	}
	private _blockScreen() {
		const blocker = document.createElement("div");
		blocker.className = "dhx_window__overlay";
		document.body.appendChild(blocker);
		this._blocker = blocker;

		if (this.config.closable) {
			blocker.addEventListener("click", () => this.hide());
			keyManager.addHotKey("escape", () => this.hide(), this);
		}
	}
}